/**
 * Pure helpers for the shared EventPicker (type-ahead event selector).
 *
 * Why a type-ahead: the old event dropdowns loaded a fixed window
 * (`.limit(100)` / `.limit(200)` / `.limit(500)`) and filtered it in the
 * browser, so any event outside that window could never be picked — PROD had
 * 417 events against a 100-row window. The picker now searches the server,
 * pages each group with a (start_time, id) cursor ("Load more"), matches dates
 * and event ids, and loads the selected event by id — so no event is
 * unreachable, including the 26th of 26 identically-named recurring events.
 */

export interface EventPickerOption {
  id: string;
  title: string | null;
  start_time: string | null;
  status: string | null;
  type: string | null;
  location_name: string | null;
  location_city: string | null;
  location_state: string | null;
  is_deleted: boolean | null;
}

/** Columns the picker reads. Every one is verified on QA + PROD `public.events`. */
export const EVENT_PICKER_COLUMNS =
  "id, title, start_time, status, type, location_name, location_city, location_state, is_deleted";

/** How many rows each group (Upcoming / Past) shows per search. */
export const EVENT_PICKER_PAGE_SIZE = 25;

/**
 * The boundary between "Upcoming" and "Past": local midnight today, so an event
 * that started this morning is still listed under Upcoming (it is today's event).
 */
export function upcomingCutoff(now: Date = new Date()): Date {
  const d = new Date(now.getTime());
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Event date as a short local date, or "" when the event has no start time. */
export function formatEventPickerDate(startTime: string | null): string {
  if (!startTime) {
    return "";
  }
  const d = new Date(startTime);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Date AND time, with weekday and zone ("Sat, Nov 14, 2026, 6:00 PM CST"), so
 * recurring events that share a name, venue and city can be told apart.
 */
export function formatEventPickerDateTime(startTime: string | null): string {
  if (!startTime) {
    return "";
  }
  const d = new Date(startTime);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  return d.toLocaleString(undefined, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

/** Short id shown on each option; also a valid id-prefix search term. */
export const eventPickerShortId = (id: string): string => id.slice(0, 8);

/** "City, ST" (either part may be missing). */
export function formatEventPickerPlace(ev: EventPickerOption): string {
  return [ev.location_city, ev.location_state]
    .map((s) => (s ?? "").trim())
    .filter(Boolean)
    .join(", ");
}

/** Status marker shown after the title for events an operator should notice. */
export function eventPickerStatusNote(ev: EventPickerOption): string | null {
  if (ev.is_deleted === true) {
    return "deleted";
  }
  if (ev.status === "cancelled") {
    return "cancelled";
  }
  return null;
}

/**
 * One-line label:
 * "Title — Sat, Nov 14, 2026, 6:00 PM CST · Venue · Tampa, FL · ID 28b64552 (cancelled)".
 */
export function formatEventPickerLabel(ev: EventPickerOption): string {
  const title = (ev.title ?? "").trim() || "Untitled event";
  const detail = [
    formatEventPickerDateTime(ev.start_time),
    (ev.location_name ?? "").trim(),
    formatEventPickerPlace(ev),
    ev.id ? `ID ${eventPickerShortId(ev.id)}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const note = eventPickerStatusNote(ev);
  return `${title}${detail ? ` — ${detail}` : ""}${note ? ` (${note})` : ""}`;
}

// ---------------------------------------------------------------------------
// Search-term parsing. Both parsers return VALUES the service writes into the
// PostgREST filter itself (ISO timestamps, canonical lowercase UUIDs) — never
// the user's raw text — so they cannot inject filter grammar.
// ---------------------------------------------------------------------------

/** One local calendar day: [start, end). */
export interface EventPickerDayRange {
  start: Date;
  end: Date;
}

const MONTHS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];

function monthFromName(name: string): number | null {
  const n = name.toLowerCase().replace(/\.$/, "");
  if (n.length < 3) {
    return null;
  }
  const i = MONTHS.findIndex((m) => n.startsWith(m));
  if (i < 0) {
    return null;
  }
  // Reject words that merely start like a month ("marathon", "mayor").
  const full = [
    "january",
    "february",
    "march",
    "april",
    "may",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december",
  ][i];
  const ok = n === MONTHS[i] || n === full || (i === 8 && n === "sept");
  return ok ? i + 1 : null;
}

function dayRange(y: number, m: number, d: number): EventPickerDayRange | null {
  const start = new Date(y, m - 1, d, 0, 0, 0, 0);
  if (
    start.getFullYear() !== y ||
    start.getMonth() !== m - 1 ||
    start.getDate() !== d
  ) {
    return null; // e.g. Feb 30
  }
  const end = new Date(y, m - 1, d + 1, 0, 0, 0, 0);
  return { start, end };
}

/**
 * Parse a date search: "2026-11-14", "11/14/2026", "11/14/26", "11/14",
 * "Nov 14", "Nov 14 2026", "November 14, 2026", "14 Nov 2026" (case-insensitive,
 * optional ordinal suffix). Without a year it matches that day in last year,
 * this year and next year. Returns [] when the term is not a date.
 */
export function parseEventDateQuery(
  raw: string,
  now: Date = new Date(),
): EventPickerDayRange[] {
  const t = raw.trim().toLowerCase().replace(/\s+/g, " ");
  if (!t) {
    return [];
  }
  let y: number | null = null;
  let m: number | null = null;
  let d: number | null = null;
  let r: RegExpExecArray | null;
  if ((r = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t))) {
    [y, m, d] = [Number(r[1]), Number(r[2]), Number(r[3])];
  } else if ((r = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{4}|\d{2}))?$/.exec(t))) {
    [m, d] = [Number(r[1]), Number(r[2])];
    if (r[3]) {
      y = r[3].length === 2 ? 2000 + Number(r[3]) : Number(r[3]);
    }
  } else if (
    (r = /^([a-z]+\.?) (\d{1,2})(?:st|nd|rd|th)?(?:,? (\d{4}))?$/.exec(t))
  ) {
    m = monthFromName(r[1]);
    d = Number(r[2]);
    y = r[3] ? Number(r[3]) : null;
  } else if (
    (r = /^(\d{1,2})(?:st|nd|rd|th)? ([a-z]+\.?)(?:,? (\d{4}))?$/.exec(t))
  ) {
    d = Number(r[1]);
    m = monthFromName(r[2]);
    y = r[3] ? Number(r[3]) : null;
  }
  if (m === null || d === null || m < 1 || m > 12 || d < 1 || d > 31) {
    return [];
  }
  const years =
    y !== null
      ? [y]
      : [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];
  return years
    .map((yy) => dayRange(yy, m as number, d as number))
    .filter((x): x is EventPickerDayRange => x !== null);
}

/**
 * Parse an event-id search: a full UUID or a prefix of at least 6 hex digits,
 * hyphens optional. Returns the inclusive UUID range covering every id with
 * that prefix (UUID order in PostgreSQL is byte order, i.e. hex order), or
 * null when the term is not id-like.
 */
export function parseEventIdPrefix(
  raw: string,
): { lo: string; hi: string } | null {
  const t = raw.trim().toLowerCase();
  if (!/^[0-9a-f-]+$/.test(t)) {
    return null;
  }
  const hex = t.replace(/-/g, "");
  if (hex.length < 6 || hex.length > 32) {
    return null;
  }
  const fmt = (h: string) =>
    `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  return { lo: fmt(hex.padEnd(32, "0")), hi: fmt(hex.padEnd(32, "f")) };
}
